/**
 * 수신 통화 벨소리 — 오디오 파일 없이 Web Audio로 만든다.
 *
 * ⚠ AudioContext는 반드시 모듈 스코프에 **하나만** 두고 재사용한다.
 * 예전에는 IncomingCallBanner가 마운트마다 새로 만들고 언마운트에서 close()까지 했는데,
 * 브라우저 자동재생 정책상 컨텍스트는 "사용자 제스처가 한 번이라도 있었던" 뒤에만 소리가 난다.
 * 전화가 올 때 새로 만든 컨텍스트는 그 조건을 만족한 적이 없어 suspended로 남고,
 * 결과적으로 **벨이 무음**이었다(특히 iOS Safari, 그리고 알림을 눌러 앱이 막 켜진 경우).
 *
 * 그래서 앱 어디서든 첫 제스처가 오면 그때 컨텍스트를 만들어 깨워 둔다. 리스너는 제거하지
 * 않는다 — iOS는 앱이 백그라운드로 가면 컨텍스트를 다시 재우므로, 복귀 후 첫 터치에서
 * 한 번 더 깨워야 한다.
 */

let ctx: AudioContext | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let ringing = false;

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  const AC: typeof AudioContext | undefined =
    typeof window === 'undefined' ? undefined : (window.AudioContext || (window as any).webkitAudioContext);
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    return null;
  }
  return ctx;
}

function unlock(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
}

if (typeof window !== 'undefined') {
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  window.addEventListener('pointerdown', unlock, opts);
  window.addEventListener('keydown', unlock, opts);
  window.addEventListener('touchend', unlock, opts);
}

/** 480Hz 사인파 0.8초 — 일반적인 전화 벨 톤 */
function beep(): void {
  const c = ctx;
  if (!c || !ringing) return;
  // 아직 언락 전이면 소리는 안 나지만 루프는 유지한다 (제스처가 오면 다음 주기부터 들린다)
  if (c.state === 'suspended') {
    c.resume().catch(() => {});
    return;
  }
  try {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    osc.type = 'sine';
    osc.frequency.value = 480;
    gain.gain.setValueAtTime(0.3, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.8);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + 0.8);
  } catch {
    // 소리가 안 나도 수신 배너는 계속 떠 있어야 하므로 삼킨다
  }
}

/** 2초 주기로 벨을 울린다. 이미 울리는 중이면 무시(멱등) */
export function startRinging(): void {
  if (ringing) return;
  ringing = true;
  getCtx();
  const loop = () => {
    if (!ringing) return;
    beep();
    timer = setTimeout(loop, 2000);
  };
  loop();
}

/** 벨 정지. AudioContext는 닫지 않는다 — 닫으면 다음 통화에서 다시 무음이 된다 */
export function stopRinging(): void {
  ringing = false;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}
